import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import useAssignmentEditorNavigation from 'hooks/useAssignmentEditorNavigation';
import useAutosaveController from 'hooks/useAutosaveController';
import { PageContext as QuizDraftContext } from 'pages/CreateQuiz/PageProvider';
import { quizDraftReducer, QUIZ_DRAFT_ACTION } from 'pages/CreateQuiz/quizReducer';
import { createContext, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import apiService from 'services/api';
import quizService from 'services/quiz';
import { getFeedbackSettingsErrors, hasAssignmentSettingsErrors } from 'utils/assignmentSettingsValidation';
import { AUTOSAVE_STATUS } from 'utils/autosave';
import { isRubricValid } from 'utils/helper';
import {
    buildQuizUpdatePayload,
    createQuizDraft,
    hasCompleteQuestionCriteria,
    hasUniqueQuestionCriteria,
} from 'utils/quizDraft';
import { getQuizOverview } from 'utils/quizOverview';

export const PageContext = createContext();

const getSavedAt = (quiz) => quiz?.modified_on;
const isDraftRubricValid = (draft) =>
    Boolean(draft?.form?.rubric) &&
    isRubricValid({
        rubric: draft.form.rubric,
        api_data: draft.form.api_data,
    });
const getDraftValidation = (draft, restrictions = {}) => {
    if (!draft) {
        return {
            tabErrors: { details: true, feedback: false, questions: false },
            feedbackSettingsErrors: {},
        };
    }

    const feedbackSettingsErrors =
        draft.form.aiFeedbackEnabled && !restrictions.isFeedbackFeatureDisabled
            ? getFeedbackSettingsErrors(draft.form, false, restrictions)
            : {};
    return {
        tabErrors: {
            details: !isDraftRubricValid(draft),
            feedback: hasAssignmentSettingsErrors(feedbackSettingsErrors),
            questions:
                !hasCompleteQuestionCriteria(draft.questions, draft.form.rubricType) ||
                !hasUniqueQuestionCriteria(draft.questions, draft.form.rubricType),
        },
        feedbackSettingsErrors,
    };
};
const isDraftQuizValid = (draft, restrictions) =>
    !Object.values(getDraftValidation(draft, restrictions).tabErrors).some(Boolean);

const PageProvider = ({ children }) => {
    const { id } = useParams();
    const history = useHistory();
    const { auth } = useAuth();
    const { isFeedbackFeatureDisabled, isGradingFeatureDisabled, setQuizzes } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation(['quiz', 'common', 'assignment', 'assignmentSettings']);
    const [draft, dispatch] = useReducer(quizDraftReducer, null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [submissionState, setSubmissionState] = useState(null);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [rubricDialog, setRubricDialog] = useState(null);
    const [isResyncing, setIsResyncing] = useState(false);
    const isEditable = Boolean(
        !loading &&
            submissionState?.quizId === id &&
            !submissionState.submissions.length &&
            !submissionState.batches.length
    );
    const dispatchEdit = useCallback(
        (action) => {
            if (isEditable) dispatch(action);
        },
        [isEditable]
    );
    const validationRestrictions = useMemo(
        () => ({ isFeedbackFeatureDisabled, isGradingFeatureDisabled }),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled]
    );
    const validateDraft = useCallback(
        (snapshot) => isDraftQuizValid(snapshot, validationRestrictions),
        [validationRestrictions]
    );

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadFailed(false);
        setSubmissionState(null);

        Promise.all([quizService.getQuiz(id), quizService.listQuizSubmissions(id), quizService.listQuizBatch(id)])
            .then(([result, submissions, batches]) => {
                if (!active) return;
                if (!result || !Array.isArray(submissions) || !Array.isArray(batches)) {
                    throw new Error('Invalid quiz editing data');
                }
                dispatch({
                    type: QUIZ_DRAFT_ACTION.HYDRATE,
                    draft: createQuizDraft(result, result.course_id || auth?.course_id),
                });
                setSubmissionState({ quizId: id, submissions, batches });
                setQuizzes((current) => {
                    const quizzes = current || [];
                    return quizzes.some((quiz) => quiz.id === id)
                        ? quizzes.map((quiz) => (quiz.id === id ? result : quiz))
                        : [...quizzes, result];
                });
            })
            .catch((error) => {
                if (!active) return;
                setLoadFailed(true);
                const status = error?.status ?? error?.response?.status;
                if (status === 404) {
                    setQuizzes((current) => (current || []).filter((quiz) => quiz.id !== id));
                } else {
                    message.error(t('quiz:edit.load-error', 'The quiz could not be loaded. Please try again.'));
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [auth?.course_id, id, message, setQuizzes, t]);

    const setQuizInApp = useCallback(
        (savedQuiz) => {
            if (!savedQuiz) return;
            setQuizzes((current) => {
                const quizzes = current || [];
                return quizzes.some((quiz) => quiz.id === savedQuiz.id)
                    ? quizzes.map((quiz) => (quiz.id === savedQuiz.id ? savedQuiz : quiz))
                    : [...quizzes, savedQuiz];
            });
        },
        [setQuizzes]
    );

    const showValidationDialog = useCallback(
        (snapshot) => {
            const rubric = snapshot?.form?.rubric;
            const { tabErrors } = getDraftValidation(snapshot, validationRestrictions);
            const description =
                tabErrors.details && !rubric
                    ? t('quiz:edit.validation.missing-rubric', 'Add a rubric before saving this quiz.')
                    : tabErrors.details && !rubric.title?.trim()
                      ? t('quiz:edit.validation.missing-rubric-title', 'Add a title to the quiz rubric before saving.')
                      : tabErrors.details
                        ? t(
                              'quiz:edit.validation.invalid-rates',
                              'Enter a valid non-negative point value for every rating in the quiz rubric.'
                          )
                        : tabErrors.feedback
                          ? t(
                                'quiz:edit.validation.invalid-feedback',
                                'Fix the invalid feedback settings before saving this quiz.'
                            )
                          : !hasUniqueQuestionCriteria(snapshot.questions, snapshot.form.rubricType)
                            ? t(
                                  'quiz:edit.validation.duplicate-question-criteria',
                                  'Each criterion can only be mapped to one question. Remove duplicate mappings before saving.'
                              )
                            : t(
                                  'quiz:edit.validation.missing-question-criteria',
                                  'Select at least one criterion for every written-response question before saving.'
                              );
            setDialog({
                open: true,
                message: t('quiz:edit.validation.title', 'Quiz cannot be saved'),
                description,
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: closeDialog,
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        },
        [closeDialog, setDialog, t, validationRestrictions]
    );

    const persist = useCallback(
        (snapshot) =>
            quizService.updateQuiz(
                id,
                buildQuizUpdatePayload({
                    draft: snapshot,
                    isFeedbackFeatureDisabled,
                })
            ),
        [id, isFeedbackFeatureDisabled]
    );

    const handleSaveError = useCallback(
        () => message.error(t('quiz:edit.save-error', 'The quiz could not be saved. Please try again.')),
        [message, t]
    );

    const autosave = useAutosaveController({
        value: draft,
        enabled: isEditable,
        autoSaveEnabled: isEditable,
        debounceMs: 800,
        initialLastSavedAt: draft?.quiz?.modified_on,
        persist,
        validate: validateDraft,
        onInvalid: showValidationDialog,
        onSuccess: setQuizInApp,
        onError: handleSaveError,
        getSavedAt,
    });

    const onClickBack = useAssignmentEditorNavigation({
        isDirty: autosave.isDirty,
        isSaving: autosave.status === AUTOSAVE_STATUS.SAVING,
        backPath: `/quizzes/${id}`,
    });

    const setForm = useCallback(
        (update) => dispatchEdit({ type: QUIZ_DRAFT_ACTION.UPDATE_FORM, update }),
        [dispatchEdit]
    );
    const setQuiz = useCallback(
        (update) => dispatchEdit({ type: QUIZ_DRAFT_ACTION.UPDATE_QUIZ, update }),
        [dispatchEdit]
    );
    const quiz = useMemo(
        () => (draft?.quiz ? { ...draft.quiz, questions: draft.questions } : undefined),
        [draft?.questions, draft?.quiz]
    );
    const overview = useMemo(() => getQuizOverview(quiz), [quiz]);
    const validation = useMemo(
        () => getDraftValidation(draft, validationRestrictions),
        [draft, validationRestrictions]
    );
    const isQuizValid = !Object.values(validation.tabErrors).some(Boolean);

    const handleRubricTypeChange = (rubricType) => {
        dispatchEdit({ type: QUIZ_DRAFT_ACTION.SET_RUBRIC_TYPE, rubricType });
    };

    const handleSaveRubric = (rubric) => {
        dispatchEdit({ type: QUIZ_DRAFT_ACTION.SAVE_RUBRIC, rubric });
    };

    const handleDeleteRubric = () => {
        if (!isEditable) return;
        setDialog({
            open: true,
            message: t('quiz:dialog.delete-rubric.title', 'Delete Rubric?'),
            description: t('quiz:dialog.delete-rubric.description', 'This action cannot be undone.'),
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                dispatchEdit({ type: QUIZ_DRAFT_ACTION.DELETE_RUBRIC });
                closeDialog();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    const handleSyncedRubric = ({ api_data, rubric }) => {
        dispatchEdit({ type: QUIZ_DRAFT_ACTION.SYNC_RUBRIC, api_data, rubric });
        setRubricDialog(null);
    };

    const handleMapCriteriaByOrderChange = (checked) => {
        dispatchEdit({ type: QUIZ_DRAFT_ACTION.SET_MAP_CRITERIA_BY_ORDER, checked });
    };

    const handleCriterionIdsChange = (questionId, criterionIds) => {
        dispatchEdit({
            type: QUIZ_DRAFT_ACTION.UPDATE_QUESTION,
            questionId,
            changes: { criterion_ids: criterionIds },
        });
    };

    const handleGradingNotesChange = (questionId, notes) => {
        dispatchEdit({
            type: QUIZ_DRAFT_ACTION.UPDATE_QUESTION,
            questionId,
            changes: { grading_notes: notes },
        });
    };

    const handleResyncQuiz = async () => {
        if (!isEditable || !auth?.course_id || !draft?.form.api_assignment_id || isResyncing) return;
        setIsResyncing(true);
        try {
            const syncedQuiz = await apiService.getQuiz({
                access_token: localStorage.getItem('access_token'),
                course_id: auth.course_id,
                quiz_id: draft.form.api_assignment_id,
            });
            if (!syncedQuiz || !Array.isArray(syncedQuiz.questions)) throw new Error('Invalid LMS quiz details');
            dispatchEdit({
                type: QUIZ_DRAFT_ACTION.RESYNC_QUIZ,
                quiz: syncedQuiz,
                courseId: auth.course_id,
            });
            message.success(t('quiz:message.resync-success', 'Quiz questions were resynced from your LMS.'));
        } catch {
            message.error(t('quiz:message.resync-error', 'Quiz questions could not be resynced. Please try again.'));
        } finally {
            setIsResyncing(false);
        }
    };

    const handleSaveFeedback = ({ feedbackCustomContext, ...feedbackSettings }) => {
        dispatchEdit({
            type: QUIZ_DRAFT_ACTION.SAVE_FEEDBACK,
            feedbackSettings,
            feedbackCustomContext,
        });
    };

    const handleDeleteQuiz = () => {
        autosave.pause();
        setDialog({
            open: true,
            message: t('quiz:dialog.delete.title', 'Delete Quiz?'),
            description: t('quiz:dialog.delete.description', 'This action cannot be undone.'),
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                try {
                    await autosave.waitForIdle();
                    await quizService.deleteQuiz(id);
                    setQuizzes((current) => (current || []).filter((item) => item.id !== id));
                    closeDialog();
                    history.push('/quizzes');
                } catch {
                    autosave.resume();
                    message.error(t('quiz:message.delete-error', 'The quiz could not be deleted. Please try again.'));
                }
            },
            onCancel: () => {
                autosave.resume();
                closeDialog();
            },
            onClose: () => {
                autosave.resume();
                closeDialog();
            },
        });
    };

    const contextValue = {
        draft,
        quiz,
        questions: draft?.questions || [],
        overview,
        form: draft?.form,
        setForm,
        setQuiz,
        loading,
        loadFailed,
        isEditable,
        autosaveStatus: autosave.status,
        lastSavedAt: autosave.lastSavedAt || draft?.quiz?.modified_on,
        isQuizValid,
        tabErrors: validation.tabErrors,
        feedbackSettingsErrors: validation.feedbackSettingsErrors,
        onClickBack,
        onClickSave: autosave.save,
        onClickDelete: handleDeleteQuiz,
        isResyncing,
        handleResyncQuiz,
        rubricDialog,
        setRubricDialog,
        handleRubricTypeChange,
        handleMapCriteriaByOrderChange,
        handleCriterionIdsChange,
        handleGradingNotesChange,
        handleSaveRubric,
        handleDeleteRubric,
        handleSyncedRubric,
        feedbackDialogOpen,
        setFeedbackDialogOpen,
        handleSaveFeedback,
    };

    return (
        <PageContext.Provider value={contextValue}>
            <QuizDraftContext.Provider value={contextValue}>{children}</QuizDraftContext.Provider>
        </PageContext.Provider>
    );
};

export default PageProvider;
